import React, { KeyboardEvent, MouseEvent } from 'react';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartO } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { Sitter } from 'us-web-services';
import { isEnterKey } from '../utils/a11y';

interface Props {
  sitter: Sitter;
  size?: number;
  onPlayVideo?: (sitter: Sitter) => void;
  onToggleFavorite?: (sitter: Sitter) => void | Promise<void>;
}

const stop = (event: MouseEvent | KeyboardEvent): void => {
  event.stopPropagation();
};

export const SitterSearchPhoto = ({
  sitter,
  size = 100,
  onPlayVideo,
  onToggleFavorite,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { firstName, pictureUrl, vimeoId, relationship } = sitter;
  const blocked = relationship?.blocked;
  const bookmarked = relationship?.bookmarked;
  const showVideo = !blocked && Boolean(vimeoId) && Boolean(onPlayVideo);
  const showHeart = !blocked && Boolean(onToggleFavorite);

  const handleVideo = (event: MouseEvent | KeyboardEvent): void => {
    stop(event);
    onPlayVideo?.(sitter);
  };

  const handleFavorite = (event: MouseEvent | KeyboardEvent): void => {
    stop(event);
    onToggleFavorite?.(sitter);
  };

  return (
    <div
      className='relative flex-shrink-0'
      style={{ width: size, height: size }}
    >
      <img
        alt={firstName ?? 'Caregiver photo'}
        src={pictureUrl}
        className='rounded-full object-cover w-full h-full'
        width={size}
        height={size}
      />
      {blocked && (
        <span
          id={`blocked-overlay-${sitter.id ?? ''}`}
          className='absolute inset-0 flex items-center justify-center rounded-full bg-white/80'
        >
          <FontAwesomeIcon icon={faEyeSlash} className='text-3xl text-gray-600' />
        </span>
      )}
      {showVideo && (
        <button
          type='button'
          id={`play-video-${sitter.id ?? ''}`}
          aria-label={t('components.sitterSearchCard.playVideoAria', { sitterName: firstName ?? '' })}
          onClick={handleVideo}
          onKeyDown={(event): void => { if (isEnterKey(event)) handleVideo(event); }}
          className='absolute -bottom-1 left-0 flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50'
        >
          <FontAwesomeIcon icon={faPlay} className='text-sapphire text-sm' />
        </button>
      )}
      {showHeart && (
        <button
          type='button'
          id={`favorite-sitter-${sitter.id ?? ''}`}
          aria-label={t(bookmarked
            ? 'components.sitterSearchCard.unfavoriteAria'
            : 'components.sitterSearchCard.favoriteAria',
          { sitterName: firstName ?? '' })}
          onClick={handleFavorite}
          onKeyDown={(event): void => { if (isEnterKey(event)) handleFavorite(event); }}
          className='absolute -bottom-1 right-0 flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-50'
        >
          <FontAwesomeIcon
            icon={bookmarked ? faHeart : faHeartO}
            className={`text-base ${bookmarked ? 'text-bubblegum' : 'text-gray-500'}`}
          />
        </button>
      )}
    </div>
  );
};
